import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { summaryCardTheme as t } from "./styles";

const formatCurrency = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "$0.00";

const getTotalFailedControls = (overall) => {
  if (!overall || !Array.isArray(overall.compliance_by_framework)) return overall?.compliance_failed_controls_total ?? 0;
  return overall.compliance_by_framework.reduce(
    (sum, fw) => sum + (Number(fw.controls_failed_total) || 0),
    0
  );
};

/** Prefer breaks after "+" (ZWSP); tie " … Findings" (NBSP) so "Cost Findings" and the ⓘ stay one visual group when possible. */
const formatKpiLabelDisplay = (label) =>
  typeof label === "string"
    ? label.replace(/\+/g, "+\u200b").replace(/ Findings/g, "\u00a0Findings")
    : label;

/** filterKey: stable id for ARN details table filtering (Summary page); omitted = not clickable */
const kpiConfig = (overall) => [
  {
    filterKey: "total_arns",
    label: "Total ARNs with Findings",
    value: overall?.arns_total ?? 0,
    tooltip: "Total number of ARNs scanned across all services",
  },
  {
    filterKey: "security",
    label: "Security Findings",
    value: overall?.arns_security_failed ?? 0,
    tooltip: "ARNs with failed security checks (CSPM)",
    accent: "issue",
  },
  {
    filterKey: "compliance_controls",
    label: "Control ID Findings",
    value: getTotalFailedControls(overall),
    tooltip: "Total failed compliance controls across all frameworks",
    accent: "warning",
  },
  {
    filterKey: "compliance_framework",
    label: "Compliance Framework Findings",
    value: overall?.compliance_frameworks_failed_count ?? null,
    tooltip: "Number of compliance frameworks with at least one failed control",
    emptyLabel: "—",
  },
  {
    filterKey: "cloud_waste",
    label: "Cloud Waste Findings",
    value: overall?.arns_cloud_waste ?? 0,
    tooltip: "ARNs eligible for estimated monthly cost savings from Cloud Waste findings",
    /** Second row: total estimated monthly savings (USD) from summary API */
    secondaryLabel: "Est. Monthly Savings",
    secondaryValue: overall?.cloud_waste_savings_total,
    secondaryFormatter: formatCurrency,
    secondaryEmptyLabel: "—",
    accent: "success",
  },
  {
    filterKey: "cost",
    label: "Cost Findings",
    value: overall?.cost_unblended_mtd_total ?? 0,
    formatter: formatCurrency,
    tooltip: "Unblended cost for the last 30 days across scanned ARNs",
    accent: "success",
  },
  {
    filterKey: "arns_both",
    label: "Security+Compliance Findings",
    value: overall?.arns_both ?? 0,
    tooltip: "ARNs common to CSPM (security) and Compliance findings",
    accent: "issue",
  },
  {
    filterKey: "arns_all_three",
    label: "Security+Compliance+Cost Findings",
    value: overall?.arns_all_three ?? 0,
    tooltip: "ARNs common to CSPM, Compliance and Cost Insight findings",
    accent: "issue",
  },
  {
    filterKey: "arns_all_four",
    label: "Security+Compliance+Cost+Cloud Waste Findings",
    value: overall?.arns_all_four ?? 0,
    tooltip: "ARNs common to CSPM, Compliance, Cost Insight and Cloud Waste",
    accent: "issue",
  },
];

function SummaryKpiCards({ overall, compact, activeFilterKey, onFilterClick, emphasizeSelection = false }) {
  const items = kpiConfig(overall);
  const clickable = typeof onFilterClick === "function";

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, minmax(0, 1fr))",
      sm: "repeat(3, minmax(0, 1fr))",
      md: "repeat(4, minmax(0, 1fr))",
      // Compact strip: minimum column width so zoomed / narrowed viewports scroll horizontally instead of clipping values.
      lg: compact ? "repeat(9, minmax(4.25rem, 1fr))" : "repeat(4, minmax(0, 1fr))",
      xl: compact ? "repeat(9, minmax(4.25rem, 1fr))" : "repeat(5, minmax(0, 1fr))",
    },
    gap: compact ? 1 : 2,
    minWidth: 0,
  };

  const grid = (
    <Box sx={gridSx}>
      {items.map((item) => {
        const hasValue = item.value != null && item.value !== "" && (typeof item.value !== "number" || !Number.isNaN(item.value));
        const displayValue =
          item.emptyLabel != null && !hasValue
            ? item.emptyLabel
            : typeof item.formatter === "function"
            ? item.formatter(item.value)
            : item.value;
        const isCurrencyPrimary = item.formatter === formatCurrency;
        const color = isCurrencyPrimary
          ? t.colors.dollar
          : item.accent === "issue"
            ? t.colors.issue
            : item.accent === "warning"
              ? t.colors.warning
              : item.accent === "success"
                ? t.colors.success
                : t.colors.text;

        const hasSecondary =
          item.secondaryLabel != null &&
          (item.secondaryValue != null || item.secondaryEmptyLabel != null);
        const secondaryNum =
          item.secondaryValue != null && item.secondaryValue !== ""
            ? Number(item.secondaryValue)
            : NaN;
        const hasSecondaryNumber = Number.isFinite(secondaryNum);
        const displaySecondary =
          typeof item.secondaryFormatter === "function" && hasSecondaryNumber
            ? item.secondaryFormatter(secondaryNum)
            : hasSecondaryNumber
            ? String(secondaryNum)
            : item.secondaryEmptyLabel ?? "—";

        const tooltipContent = hasSecondary ? (
            <Box sx={{ p: 1, maxWidth: "min(320px, 80vw)" }}>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.78rem", color: "text.primary", lineHeight: 1.35 }}
              >
                {item.tooltip}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  mt: 0.85,
                  lineHeight: 1.35,
                }}
              >
                <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                  {item.secondaryLabel}:
                </Box>{" "}
                {displaySecondary}
              </Typography>
            </Box>
          ) : (
            item.tooltip
          );

        const isActive = clickable && item.filterKey && activeFilterKey === item.filterKey;
        const isClickableCard = clickable && item.filterKey;
        const fadeKpiPeer =
          emphasizeSelection && !compact && activeFilterKey && !isActive;

        return (
          <Box
            key={item.label}
            onClick={
              isClickableCard
                ? () => onFilterClick(item.filterKey)
                : undefined
            }
            role={isClickableCard ? "button" : undefined}
            tabIndex={isClickableCard ? 0 : undefined}
            onKeyDown={
              isClickableCard
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onFilterClick(item.filterKey);
                    }
                  }
                : undefined
            }
            sx={{
              position: "relative",
              zIndex: emphasizeSelection && isActive && !compact ? 1 : 0,
              backgroundColor: compact ? "transparent" : t.colors.card,
              border: compact
                ? "none"
                : `1px solid ${isActive ? t.colors.accent : t.colors.border}`,
              borderRadius: t.radius.lg,
              p: compact ? 0.5 : 2,
              minWidth: 0,
              pb: compact && hasSecondary ? 0.35 : undefined,
              boxShadow:
                compact
                  ? "none"
                  : emphasizeSelection && isActive
                    ? `0 0 0 1px ${t.colors.accent}, 0 8px 22px rgba(59, 130, 246, 0.2)`
                    : t.shadow,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease, transform 0.2s ease",
              cursor: isClickableCard ? "pointer" : "default",
              outline: isActive ? `2px solid ${t.colors.accent}` : "none",
              outlineOffset: isActive ? 1 : 0,
              opacity: fadeKpiPeer ? 0.5 : 1,
              transform: emphasizeSelection && isActive && !compact ? "scale(1.02)" : "none",
              "&:hover": compact
                ? {}
                : {
                    borderColor: isActive ? t.colors.accent : t.colors.borderLight,
                    boxShadow:
                      emphasizeSelection && isActive
                        ? `0 0 0 1px ${t.colors.accent}, 0 10px 26px rgba(59, 130, 246, 0.24)`
                        : "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
                  },
            }}
          >
            {/* Inline label + icon: avoids a full-width text box that parks the ⓘ on the cell edge; ZWSP/NBSP on string handles breaks. */}
            <Box
              sx={{
                mb: compact ? 0.35 : 0.5,
                minWidth: 0,
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: t.colors.text,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  lineHeight: 1.25,
                }}
              >
                {formatKpiLabelDisplay(item.label)}
              </Typography>
              <Tooltip
                title={tooltipContent}
                arrow
                placement="top"
                enterDelay={200}
                leaveDelay={0}
                PopperProps={{
                  modifiers: [
                    {
                      name: "preventOverflow",
                      options: { boundary: "viewport", padding: 8 },
                    },
                    {
                      name: "flip",
                      options: { fallbackPlacements: ["top", "bottom", "right", "left"] },
                    },
                  ],
                }}
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 3,
                      pointerEvents: "auto",
                      maxWidth: "min(320px, 80vw)",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxHeight: "none",
                      overflow: "hidden",
                      overflowX: "hidden",
                      "& .MuiTooltip-arrow": {
                        color: "background.paper",
                        "&::before": {
                          border: "1px solid",
                          borderColor: "divider",
                        },
                      },
                    },
                  },
                }}
              >
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  aria-label={`${item.label} info`}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    verticalAlign: "text-top",
                    ml: 0.35,
                    mt: compact ? "1px" : 0,
                    cursor: "pointer",
                    color: t.colors.textMuted,
                    lineHeight: 0,
                    "&:hover": { color: t.colors.text },
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ fontSize: compact ? 14 : 16 }} />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  color,
                  /* Match FinOps (`CostInsight`) value rows */
                  fontWeight: "bold",
                  fontSize: compact ? "0.75rem" : "1rem",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: compact ? 1.25 : 1.2,
                }}
              >
                {displayValue}
              </Typography>
              {hasSecondary && (
                <Box
                  sx={{
                    mt: compact ? 0.35 : 0.75,
                    pt: compact ? 0.35 : 0.65,
                    borderTop: `1px solid ${t.colors.borderLight}`,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: compact ? "0.52rem" : "0.625rem",
                      fontWeight: "bold",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: t.colors.textMuted,
                      lineHeight: 1.2,
                      mb: 0.15,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {compact ? "Est. Mo. savings" : item.secondaryLabel}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: compact ? "0.75rem" : "0.9375rem",
                      fontWeight: "bold",
                      fontVariantNumeric: "tabular-nums",
                      color: t.colors.dollar,
                      lineHeight: 1.2,
                    }}
                  >
                    {displaySecondary}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  if (!compact) return grid;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        overflowY: "visible",
        WebkitOverflowScrolling: "touch",
        mx: -0.25,
        px: 0.25,
      }}
    >
      {grid}
    </Box>
  );
}

export default SummaryKpiCards;
